import { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import { ApiPath } from "..";
import { ApiResponse } from "../public-types";
import {
  CUSTOM_FIELD_TARGET_TYPE,
  PartnerMemberCustomFieldModel,
  PartnerMemberCustomFieldScope,
  PartnerMemberCustomFieldSetting,
  PartnerMemberExtraInfo,
  PartnerMemberInputSaveItem,
  PartnerMemberListModel,
  PartnerMemberListParam,
} from "./userAdditionalInfo-type";

/**
 * RCR11. 이용자 부가정보 커스텀 필드 목록 조회
 * - 계약그룹과 동일한 custom-fields 리소스를 targetType=PARTNER_MEMBER 로 스코프 구분
 * - data.setting.isAppVisible: 아이콘 출력 여부 (저장된 설정이 없으면 false)
 * - {partnerId} 는 axios 인터셉터에서 주입
 */
export const getPartnerMemberCustomFieldsAsync = async (
  axios: AxiosInstance,
  params?: any,
): Promise<AxiosResponse<ApiResponse<PartnerMemberCustomFieldScope>>> => {
  return await axios.get(ApiPath.contractGroup.customField.list, {
    params: { ...params, targetType: CUSTOM_FIELD_TARGET_TYPE.PARTNER_MEMBER },
  });
};

/**
 * RCR12. 이용자 부가정보 커스텀 필드 저장 (등록 / 수정 / 삭제 + 아이콘 출력 설정)
 * - 노출 선후관계 위반 시 서버에서 eCT117 오류 처리
 *   (isVisible=true 여야 isAppVisible=true, isAppVisible=true 여야 isAppEditable=true 가능)
 * - 요청에 포함된 fieldId 만 갱신되므로 전체 목록 전송 불요
 * - setting 은 지정한 경우에만 변경 (미지정 = 설정 변경 없음)
 *   → 아이콘 토글만 저장할 때는 customFieldList 없이 호출 가능
 */
export const updatePartnerMemberCustomFieldsAsync = async (
  axios: AxiosInstance,
  params?: { customFieldList?: PartnerMemberCustomFieldModel[]; setting?: PartnerMemberCustomFieldSetting },
): Promise<AxiosResponse<ApiResponse<{}>>> => {
  return await axios.post(ApiPath.contractGroup.customField.add, {
    targetType: CUSTOM_FIELD_TARGET_TYPE.PARTNER_MEMBER,
    ...(params?.customFieldList ? { customFieldList: params.customFieldList } : {}),
    ...(params?.setting ? { setting: params.setting } : {}),
  });
};

/**
 * RCR31. 파트너회원(이용자 부가정보) 목록 조회
 * - 회원정보 + 신청계약 요약 + 커스텀필드 입력값(노출 필드) 을 행에 포함
 * - 탈퇴 회원(탈퇴요청 포함) 은 서버에서 제외
 * - {partnerId} 는 axios 인터셉터에서 주입
 */
export const getPartnerMemberListAsync = async (
  axios: AxiosInstance,
  params?: PartnerMemberListParam,
): Promise<AxiosResponse<ApiResponse<{ content: PartnerMemberListModel[] }>>> => {
  return await axios.get(ApiPath.userAdditionalInfo.list, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
};

/**
 * RCR32. 파트너회원 부가정보 상세 조회
 * - 회원정보(member) + 커스텀필드 입력값(inputList, 노출 필드만) 반환
 * - 미등록·탈퇴(탈퇴요청 포함) 회원 조회 시 eCT501 (404)
 * - {partnerId} 는 인터셉터 주입, {memberNo} 는 직접 치환
 */
export const getPartnerMemberExtraInfoAsync = async (
  axios: AxiosInstance,
  params?: { memberNo: string },
): Promise<AxiosResponse<ApiResponse<PartnerMemberExtraInfo>>> => {
  return await axios.get(ApiPath.userAdditionalInfo.detail.replace("{memberNo}", params?.memberNo || ""));
};

/**
 * RCR33. 파트너회원 부가정보 저장 (커스텀필드 입력값 등록/수정/삭제)
 * - inputList: fieldId 기준 등록/수정, isDeleted=true 시 삭제
 * - 요청에 없는 fieldId 는 변경되지 않음
 */
export const savePartnerMemberExtraInfoAsync = async (
  axios: AxiosInstance,
  params?: { memberNo: string; inputList: PartnerMemberInputSaveItem[] },
): Promise<AxiosResponse<ApiResponse<{}>>> => {
  return await axios.post(ApiPath.userAdditionalInfo.save.replace("{memberNo}", params?.memberNo || ""), {
    inputList: params?.inputList || [],
  });
};
